import * as v from 'valibot';

import { vcEnumCapitalCase, vcGcid, vcOptionalWithNullToUndefined } from '../../../../utils/valibot/custom-types';
import { newSchema } from '../../../../utils/valibot/schema';

export enum EsoCrmCustomerVehicleRelationType {
  USER = 'User',
  OWNER = 'Owner',
  KEEPER = 'Keeper',
}

export enum EsoCrmPreferredInformation {
  BUSINESS = 'Business',
  PRIVATE = 'Private',
}

export enum EsoCrmPreferredPhone {
  PRIVATE_MOBILE = 'Private Mobile',
  BUSINESS_MOBILE = 'Business Mobile',
  PRIVATE_LANDLINE = 'Private Landline',
  BUSINESS_LANDLINE = 'Business Landline',
}

export enum EsoCrmCustomerMainType {
  PERSON = 'Person',
  ORGANIZATION = 'Organization',
}

export enum EsoCrmDocumentType {
  PER_IDE_DRIVER_LICENCE = 'PER_IDE_DRIVER_LICENCE',
  PER_IDE_DRIVER_LICENSE_PASSPORT_ID = 'PER_IDE_DRIVER_LICENSE_PASSPORT_ID',
  PER_IDE_IDENTIFICATION_NUMBER = 'PER_IDE_IDENTIFICATION_NUMBER',
  PER_IDE_IDENTITY_CARD = 'PER_IDE_IDENTITY_CARD',
  PER_IDE_PASSPORT = 'PER_IDE_PASSPORT',
  PER_IDE_PASSPORT_ID = 'PER_IDE_PASSPORT_ID',
  PER_IDE_PERSON_ID = 'PER_IDE_PERSON_ID',
  PER_IDE_SOCIAL_SECURITY_NUMBER = 'PER_IDE_SOCIAL_SECURITY_NUMBER',
  PER_IDE_SOUTH_AFRICA_NATIONAL_ID = 'PER_IDE_SOUTH_AFRICA_NATIONAL_ID',
}

export const EsoCrmAddressSchema = newSchema(
  'EsoCrmAddressSchema',
  v.object({
    street: vcOptionalWithNullToUndefined(v.string()),
    houseNumber: vcOptionalWithNullToUndefined(v.string()),
    postCode: vcOptionalWithNullToUndefined(v.string()),
    city: vcOptionalWithNullToUndefined(v.string()),
    country: vcOptionalWithNullToUndefined(v.string()),
    state: vcOptionalWithNullToUndefined(v.string()),
    addressLine1: vcOptionalWithNullToUndefined(v.string()),
    addressLine2: vcOptionalWithNullToUndefined(v.string()),
  }),
);

export const AddressesSchema = newSchema(
  'AddressesSchema',
  v.object({
    privateAddress: vcOptionalWithNullToUndefined(EsoCrmAddressSchema),
    businessAddress: vcOptionalWithNullToUndefined(EsoCrmAddressSchema),
    preferredAddress: vcOptionalWithNullToUndefined(v.enum(EsoCrmPreferredInformation)),
  }),
);

export const CommunicationsSchema = newSchema(
  'CommunicationsSchema',
  v.object({
    email: vcOptionalWithNullToUndefined(v.pipe(v.string(), v.email())),
    landlinePhone: vcOptionalWithNullToUndefined(v.string()),
    mobilePhone: vcOptionalWithNullToUndefined(v.string()),
  }),
);

export const IndividualCommunicationsSchema = newSchema(
  'IndividualCommunicationsSchema',
  v.object({
    privateCommunications: vcOptionalWithNullToUndefined(CommunicationsSchema),
    businessCommunications: vcOptionalWithNullToUndefined(CommunicationsSchema),
  }),
);

export const IndividualDetailsSchema = newSchema(
  'IndividualDetailsSchema',
  v.object({
    nationalId1: vcOptionalWithNullToUndefined(v.string()),
  }),
);

export const IndividualNameSchema = newSchema(
  'IndividualNameSchema',
  v.object({
    firstName: vcOptionalWithNullToUndefined(v.string()),
    lastName: vcOptionalWithNullToUndefined(v.string()),
    middleName: vcOptionalWithNullToUndefined(v.string()),
    suffix: vcOptionalWithNullToUndefined(v.string()),
  }),
);

/**
 * Customer data returned by the ESO CRM vehicle search (CUSTAPP-607: GB/IE only).
 * Reference: https://atc.bmwgroup.net/confluence/display/ESOCRM/Object%3A+Account
 */
export const EsoCrmCustomerDataSchema = newSchema(
  'EsoCrmCustomerDataSchema',
  v.object({
    gcid: vcGcid,
    bmwMiniId: vcOptionalWithNullToUndefined(v.string()),
    customerMainType: vcOptionalWithNullToUndefined(vcEnumCapitalCase(EsoCrmCustomerMainType)),
    documentType: vcOptionalWithNullToUndefined(v.enum(EsoCrmDocumentType)),
    individualAddresses: vcOptionalWithNullToUndefined(AddressesSchema),
    individualCommunications: vcOptionalWithNullToUndefined(IndividualCommunicationsSchema),
    individualDetails: vcOptionalWithNullToUndefined(IndividualDetailsSchema),
    individualName: vcOptionalWithNullToUndefined(IndividualNameSchema),
    preferredEmail: vcOptionalWithNullToUndefined(v.enum(EsoCrmPreferredInformation)),
    preferredPhone: vcOptionalWithNullToUndefined(vcEnumCapitalCase(EsoCrmPreferredPhone)),
    taxNumber: vcOptionalWithNullToUndefined(v.string()),
    role: vcOptionalWithNullToUndefined(v.enum(EsoCrmCustomerVehicleRelationType)),
    inSyncWithDirectSales: vcOptionalWithNullToUndefined(v.boolean()),
  }),
);

export const EsoCrmCustomerDataResponseSchema = newSchema(
  'EsoCrmCustomerDataResponseSchema',
  v.array(EsoCrmCustomerDataSchema),
);

export type EsoCrmAddress = v.InferOutput<typeof EsoCrmAddressSchema>;
export type EsoCrmCustomerData = v.InferOutput<typeof EsoCrmCustomerDataSchema>;
export type EsoCrmCustomerDataResponse = v.InferOutput<typeof EsoCrmCustomerDataResponseSchema>;
